import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Bot, FileText, Globe, Image as ImageIcon, Play, RefreshCw, Sparkles, Trash2, User } from 'lucide-react';
import PhotoDetail, { Row } from '../components/PhotoDetail';
import { GRID, PhotoCard, PhotoGridSkeleton } from '../components/admin/PhotoGrid';
import {
  Badge, Button, Card, Checkbox, ConfirmDialog, cx, EmptyState, Eyebrow, FOCUS, IconButton, Notice, PageHeader,
  Progress, Select, Spinner, TextArea
} from '../components/admin/ui';
import { AI_STATUS, BASE_URL, formatDate, formatDateTime, useApi } from '../lib/admin';

const AiBadge = ({ ai }) => {
  if (!ai) return <Badge>Sin analizar</Badge>;
  const status = AI_STATUS[ai.status] || { label: ai.status, tone: 'neutral' };
  return <Badge tone={status.tone} dot>{status.label}</Badge>;
};

const modelLabel = (model) => {
  const [provider, name] = (model || '').split('/');
  return name ? name : provider;
};

export default function AdminFolder() {
  const { uid, folderId } = useParams();
  const api = useApi();
  const owner = `?owner=${encodeURIComponent(uid)}`;
  const [folder, setFolder] = useState(null);
  const [error, setError] = useState('');
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('');
  const [redo, setRedo] = useState(false);
  const [starting, setStarting] = useState(false);
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [analysisModel, setAnalysisModel] = useState('');
  const [instructions, setInstructions] = useState('');
  const [running, setRunning] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetch(`${BASE_URL}/models/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setModels(data.models || []);
        setModel(data.default || '');
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const [detail, extra] = await Promise.all([
        api(`/admin/folders/${uid}/${folderId}/`),
        api(`/folders/${folderId}/analyses/${owner}`).catch(() => ({ analyses: [] })),
      ]);
      setFolder(detail);
      setAnalyses(extra.analyses || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [api, uid, folderId, owner]);

  useEffect(() => { load(); }, [load]);

  const photos = useMemo(() => folder?.photos || [], [folder]);
  const transcribed = photos.filter((p) => p.ai?.status === 'completed').length;
  const processing = photos.filter((p) => p.ai?.status === 'processing').length;
  const failed = photos.filter((p) => p.ai?.status === 'failed').length;
  const busy = processing > 0 || folder?.summary_status === 'generating' || analyses.some((a) => a.status === 'running');

  // Live progress while the AI is working
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [busy, load]);

  const modelOptions = useMemo(() => models.map((m) => ({ value: m, label: modelLabel(m), description: m.split('/')[0] })), [models]);

  const analyze = async () => {
    setStarting(true);
    setNotice('');
    try {
      const result = await api(`/admin/folders/${uid}/${folderId}/analyze/`, { method: 'POST', body: JSON.stringify({ model, redo }) });
      setNotice(result.queued
        ? `${result.queued} foto${result.queued !== 1 ? 's' : ''} en cola${result.skipped ? ` · ${result.skipped} ya estaban transcritas` : ''}.`
        : 'Todas las fotos ya están transcritas. Marca “Volver a transcribir todo” para repetir.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const regenerateSummary = async () => {
    try {
      await api(`/folders/${folderId}/summary/${owner}`, { method: 'POST' });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const runAnalysis = async () => {
    setRunning(true);
    try {
      await api(`/folders/${folderId}/analyses/${owner}`, {
        method: 'POST', body: JSON.stringify({ model: analysisModel || models[0], instructions }),
      });
      setInstructions('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const index = selected === null ? -1 : selected;
  const back = (
    <Link to="/admin?tab=folders" className={cx('mb-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-umber/55 transition-colors hover:text-ink', FOCUS)}>
      <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} /> Folders de la app
    </Link>
  );

  return (
    <div className="min-h-screen bg-paper font-ui text-ink">
      <div className="mx-auto max-w-7xl space-y-8 px-4 pb-24 pt-10 md:px-10 md:pt-14">
        <PageHeader
          back={back}
          eyebrow="Folder"
          title={folder ? (folder.name || <span className="italic text-umber/50">Sin nombre</span>) : '…'}
          description={folder && (
            <span className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
              <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-umber/45" strokeWidth={1.75} />{folder.user_email || uid}</span>
              <span className="inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-umber/45" strokeWidth={1.75} />{folder.region || 'Sin región'}</span>
              <span className="inline-flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5 text-umber/45" strokeWidth={1.75} />{photos.length} foto{photos.length !== 1 ? 's' : ''}</span>
              {folder.created_at && <span className="text-umber/50">Creado el {formatDate(folder.created_at)}</span>}
            </span>
          )}
        />

        {error && <Notice onClose={() => setError('')}>{error}</Notice>}

        {/* AI analysis (administrators only) */}
        <Card className="overflow-hidden">
          <div className="grid gap-6 p-6 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-maroon text-parchment"><Sparkles className="h-4 w-4" strokeWidth={1.75} /></span>
                <p className="font-serif text-2xl text-ink">Análisis de IA</p>
              </div>
              <p className="mt-2.5 max-w-xl text-sm leading-relaxed text-umber/65">
                Transcribe las fotos en el orden en que fueron tomadas, cada página con la anterior y la siguiente como contexto,
                y escribe el resumen del folder. Los resultados nunca se muestran en la app.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Select className="sm:w-64" value={model} onChange={setModel} options={modelOptions} icon={Bot} align="right" />
              <Button variant="accent" size="md" icon={Play} loading={starting || processing > 0} disabled={!photos.length} onClick={analyze}>
                {processing ? 'Analizando…' : 'Iniciar análisis'}
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-4 border-t border-umber/10 bg-paper/60 px-6 py-4 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-4">
              <div className="w-full max-w-xs"><Progress value={transcribed} total={photos.length} tone={processing ? 'warning' : 'ink'} /></div>
              <p className="whitespace-nowrap font-mono text-[11px] tabular-nums text-umber/60">
                {transcribed}/{photos.length} transcritas{processing ? ` · ${processing} en proceso` : ''}{failed ? ` · ${failed} con error` : ''}
              </p>
            </div>
            <Checkbox checked={redo} onChange={setRedo} label="Volver a transcribir todo" />
          </div>
          {notice && <div className="border-t border-umber/10 px-6 py-3"><p className="text-sm text-[#3B472C]">{notice}</p></div>}
        </Card>

        {/* Photos */}
        <div>
          <Eyebrow className="mb-4">Fotos en orden de captura</Eyebrow>
          {!folder ? (
            <PhotoGridSkeleton count={6} />
          ) : !photos.length ? (
            <EmptyState icon={ImageIcon} title="Sin fotos en la nube">Las fotos aparecerán cuando el teléfono las suba con Wi-Fi.</EmptyState>
          ) : (
            <div className={GRID}>
              {photos.map((p, i) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  index={p.index}
                  onClick={() => setSelected(i)}
                  title={formatDateTime(p.taken_at)}
                  badge={<AiBadge ai={p.ai} />}
                  footer={p.ai?.status === 'completed' ? (
                    <Link
                      to={`/dashboard/${p.ai.document_id}?owner=${encodeURIComponent(uid)}&back=${encodeURIComponent(`/admin/folders/${uid}/${folderId}`)}`}
                      className="mt-1 inline-flex items-center gap-1 text-xs text-umber/60 underline-offset-4 hover:text-ink hover:underline"
                    >
                      <FileText className="h-3 w-3" strokeWidth={1.75} /> Ver transcripción
                    </Link>
                  ) : null}
                />
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {folder && (transcribed > 0 || folder.summary) && (
          <Card className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Eyebrow>Resumen del folder</Eyebrow>
                {folder.summary_model && (
                  <p className="mt-1.5 text-xs text-umber/50">{modelLabel(folder.summary_model)} · {formatDate(folder.summary_updated_at)}</p>
                )}
              </div>
              <Button size="sm" icon={RefreshCw} disabled={folder.summary_status === 'generating'} onClick={regenerateSummary}>
                {folder.summary ? 'Regenerar' : 'Generar'}
              </Button>
            </div>
            {folder.summary_status === 'generating' ? (
              <div className="mt-6 flex items-center gap-3 text-sm text-umber/60"><Spinner className="h-4 w-4" /> Escribiendo el resumen…</div>
            ) : folder.summary ? (
              <div className="mt-5 max-w-3xl whitespace-pre-wrap text-[15px] leading-7 text-ink/90">{folder.summary}</div>
            ) : folder.summary_status === 'failed' ? (
              <div className="mt-5"><Notice>No se pudo escribir el resumen: {folder.summary_error}</Notice></div>
            ) : (
              <p className="mt-5 text-sm text-umber/50">Sin resumen todavía.</p>
            )}
          </Card>
        )}

        {/* Extra analyses with other models */}
        {folder && transcribed > 0 && (
          <Card className="p-6 md:p-8">
            <Eyebrow>Análisis con otros modelos</Eyebrow>
            <p className="mt-1.5 text-sm text-umber/60">Una segunda lectura del folder completo, independiente del resumen principal.</p>
            <div className="mt-5 grid gap-3 md:grid-cols-[260px_1fr_auto] md:items-start">
              <Select value={analysisModel || models[0] || ''} onChange={setAnalysisModel} options={modelOptions} icon={Bot} />
              <TextArea rows={1} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Instrucciones opcionales — p. ej. enfócate en menciones a quipus y quipucamayos" className="min-h-[40px] py-[9px]" />
              <Button variant="primary" icon={Play} loading={running} onClick={runAnalysis}>Ejecutar</Button>
            </div>
            {analyses.length > 0 && (
              <div className="mt-6 divide-y divide-umber/10 border-t border-umber/10">
                {analyses.map((a) => (
                  <div key={a.id} className="py-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-serif text-xl text-ink">{modelLabel(a.model_used || a.model)}</p>
                          {a.status === 'running' && <Badge tone="warning" dot>Analizando</Badge>}
                          {a.status === 'failed' && <Badge tone="danger" dot>Error</Badge>}
                        </div>
                        <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-umber/45">{formatDateTime(a.created_at)}{a.documents ? ` · ${a.documents} documentos` : ''}</p>
                        {a.instructions && <p className="mt-2 border-l-2 border-umber/20 pl-3 text-sm italic text-umber/65">{a.instructions}</p>}
                      </div>
                      <IconButton icon={Trash2} size="sm" label="Eliminar análisis" onClick={() => setDeleting(a)} className="hover:!text-maroon" />
                    </div>
                    {a.status === 'running' ? (
                      <div className="mt-4 flex items-center gap-3 text-sm text-umber/60"><Spinner className="h-4 w-4" /> Leyendo el folder…</div>
                    ) : a.status === 'failed' ? (
                      <div className="mt-4"><Notice>{a.error}</Notice></div>
                    ) : (
                      <div className="mt-4 max-w-3xl whitespace-pre-wrap text-[15px] leading-7 text-ink/90">{a.result}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {index >= 0 && photos[index] && (
        <PhotoDetail
          photo={{ ...photos[index], user_email: folder.user_email, region: folder.region, folder_name: folder.name }}
          position={`${index + 1} / ${photos.length}`}
          onClose={() => setSelected(null)}
          onPrev={index > 0 ? () => setSelected(index - 1) : null}
          onNext={index < photos.length - 1 ? () => setSelected(index + 1) : null}
        >
          <Row label="Análisis IA"><AiBadge ai={photos[index].ai} /></Row>
          {photos[index].ai?.text_preview && (
            <p className="mt-3 rounded-xl border border-umber/15 bg-paper p-4 font-serif text-[15px] italic leading-relaxed text-ink/85">
              {photos[index].ai.text_preview}
            </p>
          )}
        </PhotoDetail>
      )}
      {deleting && (
        <ConfirmDialog
          title="Eliminar análisis"
          confirmLabel="Eliminar"
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await api(`/folders/${folderId}/analyses/${deleting.id}/${owner}`, { method: 'DELETE' });
            await load();
          }}
        >
          Se eliminará el análisis hecho con {modelLabel(deleting.model_used || deleting.model)}. Las transcripciones y el resumen principal no cambian.
        </ConfirmDialog>
      )}
    </div>
  );
}
